import { request } from "../utils/request";
import { BACKEND_BASE_URL } from "./Resolver";
import { APIResource } from "./Resource";
import { DefaultSetID } from "./TemplateSets";

export class Template extends APIResource {
  id: string;
  prefix: string;
  color_space: string;
  segment_length: number;
  set_ids: string[];
  name: string;
  description: string;
  category: string;
  created_at: string;
  last_updated: string;
  status_update_interval: string;
  priority: number;

  baseURL = `${BACKEND_BASE_URL}/api/v1/templates`;
  upsertFields = ["name", "prefix", "color_space", "segment_length", "set_ids"];

  constructor({
    id,
    prefix,
    color_space,
    segment_length,
    set_ids,
    name,
    description,
    category,
    created_at,
    last_updated,
    status_update_interval,
    priority,
  }: {
    id: string;
    prefix: string;
    color_space: string;
    segment_length: number;
    set_ids: string[];
    name: string;
    description: string;
    category: string;
    created_at: string;
    last_updated: string;
    status_update_interval: string;
    priority: number;
  }) {
    super();
    this.id = id;
    this.prefix = prefix;
    this.color_space = color_space;
    this.segment_length = segment_length;
    this.set_ids = set_ids?.sort(sortDefaultSetToTop);
    this.name = name;
    this.description = description;
    this.category = category;
    this.created_at = created_at;
    this.last_updated = last_updated;
    this.status_update_interval = status_update_interval;
    this.priority = priority;
  }

  name__validation(value: string) {
    if (!value) return "Should not be empty.";
  }

  prefix__validation(value: string) {
    if (!value || value.length < 7 || !value.endsWith("/master")) {
      return "Should contain at least 7 characters and end with '/master'.";
    }
  }

  color_space__validation(value: string) {
    const validColorSpace = new RegExp("^[a-z_-]+$", "g");
    if (!value || value.length === 0 || !validColorSpace.test(value)) {
      return "Should not be empty and should contain only a through z or -.";
    }
  }

  segment_length__validation(value: number) {
    if (!value || !Number.isInteger(Number(value)) || value <= 0) {
      return "Should be a valid number greater than 0.";
    }
  }

  set_ids__validation(values: string[]) {
    if (values.length === 0) return "Should not be empty.";
  }
}

export class Templates {
  private static baseURL = `${BACKEND_BASE_URL}/api/v1/templates`;

  static async list(): Promise<Template[]> {
    const url = new URL(this.baseURL);
    const response = await request(url, { method: "GET" }, true);
    const json = await response?.json();
    return json?.items?.map((item: Template) => new Template(item)) ?? [];
  }

  static async retrieve(name: string): Promise<{ [key: string]: any }> {
    const url = new URL(`${this.baseURL}/${name}`);
    const response = await request(url, { method: "GET" }, true);
    const json = await response?.json();
    return json.template;
  }

  public static async delete(name: string, safeDelete: boolean = false): Promise<void> {
    if (safeDelete) {
      const templates = await this.list();
      const template = templates.find((t) => t.name === name);
      if (template?.id == null) return;
    }

    const url = new URL(`${this.baseURL}/${name}`);
    await request(url, { method: "DELETE" }, true);
  }
}

export const sortDefaultSetToTop = (o1: string, o2: string): number => {
  // Keeps default set at the top
  if (o1 === DefaultSetID) return -1;
  if (o2 === DefaultSetID) return 1;
  return o1.localeCompare(o2);
};
